<template>
	<div class="chat-product-card">
		<div class="product-type-pill">
			{{ productType }}
		</div>
		<div class="product-content">
			<div class="product-image">
				<img v-if="productImage" :src="productImage" :alt="productTitle" />
				<div v-else class="product-placeholder">
					<InlineSvg src="/images/icons/ic-product.svg" />
				</div>
			</div>
			<div class="product-details">
				<div class="product-title">{{ productTitle }}</div>
				<div v-if="productSubtitle" class="product-subtitle">{{ productSubtitle }}</div>
			</div>
			<div class="product-actions">
				<button class="round-button" @click="viewProduct">
					<InlineSvg src="/images/icons/ic-plus.svg" />
				</button>
			</div>
		</div>
	</div>
</template>

<script>
	import { mapGetters } from 'vuex'

	export default {
		name: 'ChatProductCard',
		props: {
			product: {
				type: Object,
				required: true,
			},
		},
		computed: {
			...mapGetters('Auth', ['user']),
			productImage() {
				// Use button.image if available, otherwise fall back to product.image
				const image = this.product?.button?.image || this.product?.product?.image
				return image
			},
			productTitle() {
				const title = this.product?.product?.title || this.product?.title || 'Product'
				return title
			},
			productSubtitle() {
				const subtitle = this.product?.product?.subtitle || this.product?.subtitle || ''
				return subtitle
			},
			productTypeMapping() {
				return {
					'digital-download': 'Digital Product',
					meeting: 'Coaching Call',
					'lead-magnet': 'Collect Emails / Applications',
					fulfillment: 'Custom Product',
					link: 'URL / Media',
					course: 'eCourse',
					membership: 'Recurring Membership',
					webinar: 'Webinar',
					community: 'Community',
					'stan-referral-link': 'Stan Affiliate Link',
				}
			},
			productType() {
				const type = this.product?.product?.type || this.product?.type
				return this.productTypeMapping[type] || 'Product'
			},
		},
		mounted() {
			// Track product view event
			this.$stanAnalytics('chat-v2-product-card-displayed', {
				meta: { user_id: this.user?.user_id, username: this.user?.username },
				props: {
					chat_id: this.activeChatId,
					product_id: this.product.id,
					product_title: this.product.product?.title || this.product.title,
					product_type: this.product.type,
					product_generation_ulid: this.product.ulid,
					platform: this.isMobile ? 'mobile' : 'desktop',
				},
			})
		},
		methods: {
			viewProduct() {
				// Emit event to parent to handle product viewing
				this.$emit('view-product', this.product)
			},
		},
	}
</script>

<style lang="scss" scoped>
	.chat-product-card {
		display: flex;
		flex-direction: column;
		padding: 12px;
		background: white;
		border: 1px solid #e5e7eb;
		border-radius: 8px;
		margin: 8px 0;
		max-width: 100%;
		box-shadow: 0 0 1px 0 rgba(20, 20, 20, 0.12), 0 4px 12px 2px rgba(20, 20, 20, 0.08);
		gap: 12px;

		.product-type-pill {
			border-radius: 10000px;
			background-color: #f2f4f8;
			display: block;
			padding: 4px 8px;
			font-family: 'Inter', sans-serif;
			font-size: 12px;
			font-weight: 400;
			line-height: 110%;
			color: #131f60;
			width: fit-content;
		}

		.product-content {
			display: flex;
			align-items: flex-start;
			gap: 12px;
		}

		.product-image {
			flex-shrink: 0;
			width: 48px;
			height: 48px;
			border-radius: 6px;
			overflow: hidden;
			background: #f3f4f6;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}

			.product-placeholder {
				width: 100%;
				height: 100%;
				display: flex;
				align-items: center;
				justify-content: center;
				color: #9ca3af;

				svg {
					width: 24px;
					height: 24px;
				}
			}
		}

		.product-details {
			flex: 1;
			min-width: 0;
			max-width: 100%;

			.product-title {
				font-family: 'Plus Jakarta Sans', sans-serif;
				font-weight: 700;
				font-size: 16px;
				color: #111827;
				margin-bottom: 8px;
				line-height: 120%;
				display: -webkit-box;
				-webkit-line-clamp: 2;
				-webkit-box-orient: vertical;
				overflow: hidden;
			}

			.product-subtitle {
				font-family: 'Inter', sans-serif;
				font-weight: 400;
				font-size: 16px;
				color: #6b7280;
				margin-bottom: 4px;
				line-height: 110%;
				display: -webkit-box;
				-webkit-line-clamp: 2;
				-webkit-box-orient: vertical;
				overflow: hidden;
			}
		}

		.product-actions {
			flex-shrink: 0;

			.round-button {
				background: #ebebff;
				border-radius: 50%;
				width: 40px;
				height: 40px;
				min-width: 40px;
				min-height: 40px;
				display: flex;
				align-items: center;
				justify-content: center;
				cursor: pointer;
				transition: all 0.2s ease;
				border: none;

				::v-deep {
					svg {
						path {
							fill: #6355ff;
						}
					}
				}

				&:hover:not(:disabled) {
					background: #f3f4f6;
					border-color: #9ca3af;
					color: #374151;
				}

				&:disabled {
					opacity: 0.6;
					cursor: not-allowed;
				}
			}
		}
	}
</style>
